import React, { useEffect } from 'react';
import styled from '@emotion/styled';
import { colors, Colors } from '@modernhealth/vital-foundation';
import { BodyTextSmall, HeadingText30 } from '@modernhealth/vital-components';
import { useTranslation } from 'react-i18next';
import isPropValid from '@emotion/is-prop-valid';

import { InternalLink } from 'src/shared/components/InternalLink';
import { captureException } from 'src/shared/helpers/captureException';

import { useTranslatedLanguage } from '../pages/CirclesOnDemandOverviewPage';
import { useGetMoreLanguagesQuery } from '../documents/getMoreLanguages.generated';

import { CirclePageSection } from './CirclePageSection';

const languageCards = [
    { backgroundColor: 'orange', base: 55, hover: 65 },
    { backgroundColor: 'red', base: 75, hover: 85 },
    { backgroundColor: 'cyan', base: 65, hover: 75 },
] as const;

const subtitlesTranslationKeys = [
    'sections.international.subtitle.check-out-circles',
    'sections.international.subtitle.discover-circles',
    'sections.international.subtitle.join-circles',
];

export const NonEnglishCirclesSection = () => {
    const { t } = useTranslation('circles');
    const { data, error } = useGetMoreLanguagesQuery();

    useEffect(() => {
        if (error) {
            captureException(error);
        }
    });
    if (!data) return null;

    return (
        <CirclePageSection
            to="/circles/languages"
            header={t('sections.by-language.header')}
            cta={t('sections.series.view_all')}
        >
            <div
                css={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    justifyContent: 'space-between',
                    gap: 16,
                    'h4:first-letter': {
                        textTransform: 'capitalize',
                    },
                }}
            >
                {data.circleMoreLanguages.slice(0, 3).map(({ languageCode }, i) => {
                    return (
                        <LanguageMenuCard
                            key={`language-card-${languageCode}`}
                            backgroundColor={`${languageCards[i].backgroundColor}${languageCards[i].base}`}
                            backgroundColorOnHover={`${languageCards[i].backgroundColor}${languageCards[i].hover}`}
                            languageCode={languageCode}
                            subtitle={subtitlesTranslationKeys[i]}
                        />
                    );
                })}
            </div>
        </CirclePageSection>
    );
};

interface LanguageMenuCardProps {
    backgroundColor: Colors;
    backgroundColorOnHover: Colors;
}

const LanguageMenuCardContainer = styled(InternalLink, {
    shouldForwardProp: isPropValid,
})<LanguageMenuCardProps>(({ backgroundColor, backgroundColorOnHover }) => ({
    flex: 1,
    padding: 24,
    minWidth: 200,
    backgroundColor: colors[backgroundColor],
    color: colors.white,
    borderRadius: 6,
    textDecoration: 'none',
    '&:hover': {
        color: colors.white,
        backgroundColor: colors[backgroundColorOnHover],
    },
}));

export const LanguageMenuCard: React.FC<
    LanguageMenuCardProps & { languageCode: string; subtitle?: string }
> = ({ languageCode, subtitle, ...props }) => {
    const { t } = useTranslation('circles');
    const language = useTranslatedLanguage(languageCode, languageCode);

    return (
        <LanguageMenuCardContainer
            {...props}
            to={`/circles/language/${languageCode}`}
            aria-label={language}
        >
            <HeadingText30 fontColor="white">{language}</HeadingText30>
            {subtitle && <BodyTextSmall>{t(subtitle, { language })}</BodyTextSmall>}
        </LanguageMenuCardContainer>
    );
};
